import { useEffect, useState } from 'react';
import { GridColDef, GridPaginationModel } from '@mui/x-data-grid';
import { asyncGetSurveys } from '../../../../../services/survey.service';
import { SurveyListActions } from './actions';
import { TableTooltip } from '../../../../components/general/table';
import { DateTooltip } from '../../../../components/general/dateTooltip';
import { SurveyFilter } from '../filters/props';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../../redux/store';
import { isOnlyViewProfile } from '../../../../../shared/types/profiles';

interface Survey {
    id: string;
    code: string;
    title: string;
    description: string;
    status: string;
    created_at: string;
    updated_at: string;
}

export const useSurveyList = (filters?: SurveyFilter) => {
    const { logUser } = useSelector((state: RootState) => state.general);
    const { type } = logUser || {};
    const [loading, setLoading] = useState(false);
    const [rows, setRows] = useState<Survey[]>([]);
    const [page, setPage] = useState<number>(0);
    const [pageSize, setPageSize] = useState<number>(10);
    const [total, setTotal] = useState<number>(0);
    const [finalFilters, setFinalFilters] = useState<SurveyFilter>({});

    const paginator = (model: GridPaginationModel) => {
        if (model.page !== page) {
            setPage(model.page);
        } else if (model.pageSize !== pageSize) {
            setPageSize(model.pageSize);
        }
    };

    const fetchSurveys = () => {
        setLoading(true);
        const searchParams: any = {
            page,
            limit: pageSize,
        };

        // Filtros específicos
        if (finalFilters.code) searchParams.code = finalFilters.code;
        if (finalFilters.title) searchParams.title = finalFilters.title;
        if (finalFilters.status)
            searchParams.statusFilter = finalFilters.status;
        if (finalFilters.startDate)
            searchParams.startDate = finalFilters.startDate;
        if (finalFilters.endDate) searchParams.endDate = finalFilters.endDate;

        asyncGetSurveys(searchParams)
            .then((res: any) => {
                setRows(res.items || []);
                setTotal(res.length || 0);
            })
            .finally(() => setLoading(false));
    };

    const columns: GridColDef<Survey>[] = [];

    if (!isOnlyViewProfile(type)) {
        columns.push({
            field: 'actions',
            headerName: 'Acciones',
            width: 220,
            sortable: false,
            disableColumnMenu: true,
            filterable: false,
            align: 'center',
            headerAlign: 'center',
            renderCell: (row) => (
                <SurveyListActions
                    id={row.row.id}
                    status={row.row.status}
                    onRefresh={fetchSurveys}
                />
            ),
        });
    }

    columns.push(
        {
            field: 'code',
            headerName: 'Código',
            minWidth: 120,
            flex: 0.3,
        },
        {
            field: 'title',
            headerName: 'Título',
            minWidth: 150,
            flex: 0.5,
            renderCell: (row) => <TableTooltip value={row.row.title} />,
        },
        {
            field: 'description',
            headerName: 'Descripción',
            minWidth: 200,
            flex: 0.6,
            renderCell: (row) => <TableTooltip value={row.row.description} />,
        },
        {
            field: 'status',
            headerName: 'Estado',
            minWidth: 100,
            flex: 0.3,
            align: 'center',
            headerAlign: 'center',
            renderCell: (row) => (
                <>{row.row.status === 'active' ? 'Activo' : 'Inactivo'}</>
            ),
        },
        {
            field: 'created_at',
            headerName: 'Fecha de creación',
            minWidth: 120,
            flex: 0.4,
            align: 'center',
            headerAlign: 'center',
            renderCell: (row) => <DateTooltip date={row.row.created_at} />,
        }
    );

    useEffect(() => {
        setPage(0);
        setFinalFilters(filters || {});
    }, [filters]);

    useEffect(() => {
        fetchSurveys();
    }, [page, pageSize, finalFilters]);

    return {
        columns,
        rows,
        page,
        pageSize,
        paginator,
        loading,
        total,
    };
};
